/**
 * Workshop version of CodeFrame.
 *
 * Props-based, no React Flow or store dependencies.
 */

import { Icons } from "@/components/ui/icons";
import { FrameHeader } from "@/workshop/components/shapes/shared/frame-header";
import { FrameFooter } from "@/workshop/components/shapes/shared/frame-footer";
import { statusColor } from "@/workshop/components/shapes/shared/frame-utils";

interface CodeFrameProps {
  title: string;
  code: string;
  language: string;
  status: "idle" | "streaming" | "error" | "done";
  agentName?: string;
  onCopy?: () => void;
  onResizeStart?: React.MouseEventHandler;
}

export function CodeFrame({
  title,
  code,
  language,
  status,
  agentName,
  onCopy,
  onResizeStart,
}: CodeFrameProps) {
  return (
    <div
      className="denker-glass-frame flex flex-col rounded-frame overflow-hidden"
      data-testid="code-frame"
    >
      <FrameHeader
        title={title}
        icon={Icons.Code}
        accentColor="bg-frame-code"
        agentName={agentName}
        statusColor={statusColor(status)}
        data-testid="code-frame-header"
      />

      <div className="scrollbar-none flex-1 overflow-auto px-3 py-2">
        <div className="mb-1.5">
          <span className="rounded bg-elevated/40 px-1.5 py-0.5 text-canvas-base font-medium text-muted">
            {language}
          </span>
        </div>

        <pre className="scrollbar-none bg-surface rounded-md p-2 font-mono text-canvas-base text-secondary overflow-x-auto leading-relaxed">
          <code>
            {code || (
              <span className="italic text-muted">No code yet</span>
            )}
            {status === "streaming" && (
              <span
                className="ml-0.5 inline-block h-3.5 w-[2px] animate-pulse bg-accent"
                aria-label="Streaming cursor"
              />
            )}
          </code>
        </pre>
      </div>

      <FrameFooter onCopy={onCopy} onResizeStart={onResizeStart} data-testid="code-frame-footer" />
    </div>
  );
}
